import React from 'react'
import { useAppSelector } from '@/redux/hooks.ts'
import { selectThemeStyle } from '@/features/setting/setting.slice.ts'

export interface ReturnPillColors {
  longBg?: string
  shortBg?: string
  longBorder?: string
  shortBorder?: string
  longText?: string
  shortText?: string
}

interface ReturnPillProps {
  // Drives sign: > 0 up, < 0 down, 0 renders flat (no triangle/background).
  value: number
  // Pre-formatted label (e.g. "1.16%"); the pill stays purely presentational.
  text: string
  colors: ReturnPillColors
  // Fixes the pill width and centres content; omit to size to content.
  width?: React.CSSProperties['width']
}

// Flat dash shown for the zero/no-data state.
const FlatIcon: React.FC<{ color?: string }> = ({ color }) => (
  <svg
    xmlns={'http://www.w3.org/2000/svg'}
    width={'8'}
    height={'2'}
    viewBox={'0 0 8 2'}
    fill={'none'}
  >
    <line y1={'1'} x2={'8'} y2={'1'} stroke={color} strokeWidth={'2'} />
  </svg>
)

const UpIcon: React.FC<{ color?: string }> = ({ color }) => (
  <svg
    xmlns={'http://www.w3.org/2000/svg'}
    width={'10'}
    height={'10'}
    viewBox={'0 0 10 10'}
    fill={'none'}
    style={{ marginBottom: '2px' }}
  >
    <path
      d={
        'M9.92736 9.17448L5.26277 0.192818C5.12926 -0.0642727 4.87216 -0.0642727 4.73723 0.192818L0.0726438 9.17448C-0.100645 9.50941 0.0555991 10 0.335417 10H9.66458C9.9444 10 10.1006 9.50941 9.92736 9.17448Z'
      }
      fill={color}
    />
  </svg>
)

const DownIcon: React.FC<{ color?: string }> = ({ color }) => (
  <svg
    xmlns={'http://www.w3.org/2000/svg'}
    width={'10'}
    height={'10'}
    viewBox={'0 0 10 10'}
    fill={'none'}
  >
    <path
      d={
        'M9.66458 0H0.335417C0.0555991 0 -0.100645 0.490595 0.0726438 0.82552L4.73723 9.80718C4.87074 10.0643 5.12784 10.0643 5.26277 9.80718L9.92736 0.82552C10.1006 0.490595 9.9444 0 9.66458 0Z'
      }
      fill={color}
    />
  </svg>
)

// Return/PnL pill with an up/down triangle, coloured by sign (long/short
// tokens).
export const ReturnPill: React.FC<ReturnPillProps> = ({
  value,
  text,
  colors,
  width,
}) => {
  const themeStyle = useAppSelector(selectThemeStyle)
  const isZero = value === 0
  const isPositive = value > 0

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: width != null ? 'center' : undefined,
        gap: 4,
        backgroundColor: isZero
          ? themeStyle.btnBgDisable
          : isPositive
            ? colors.longBg
            : colors.shortBg,
        borderRadius: themeStyle.borderRadiusSM,
        borderColor: isZero
          ? ''
          : isPositive
            ? colors.longBorder
            : colors.shortBorder,
        padding: `0 4px`,
        width,
        color: isZero
          ? themeStyle.colorTextDisabled
          : isPositive
            ? colors.longText
            : colors.shortText,
      }}
    >
      {isZero ? (
        <FlatIcon color={themeStyle.colorTextDisabled} />
      ) : isPositive ? (
        <UpIcon color={colors.longText} />
      ) : (
        <DownIcon color={colors.shortText} />
      )}

      {isZero ? 'N/A' : text}
    </div>
  )
}
